import React, { useState, useEffect, useCallback } from 'react';
import './ServingPage.css';
import BASE from '../api';
import { authFetch, clearToken } from '../auth';

function elapsed(createdAt) {
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (diff < 60) return `${diff}秒前`;
  if (diff < 3600) return `${Math.floor(diff / 60)}分鐘前`;
  return `${Math.floor(diff / 3600)}小時前`;
}

function sizeLabel(item) {
  if (!item.size) return '';
  return item.size === 'M' ? `(${item.label_large || '大'})` : `(${item.label_small || '小'})`;
}

function allServed(items) {
  return items.length > 0 && items.every(item => item.item_status === 'done');
}

export default function ServingPage() {
  const [orders, setOrders] = useState([]);
  const [lastFetch, setLastFetch] = useState(null);
  const [newIds, setNewIds] = useState(new Set());
  const [paying, setPaying] = useState(new Set());
  const [serving, setServing] = useState(new Set()); // item ids being served
  const [dailyRevenue, setDailyRevenue] = useState(null);
  const [showRevenue, setShowRevenue] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [editingError, setEditingError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE}/api/serving`);
      const data = await res.json();

      setOrders(prev => {
        const prevIds = new Set(prev.map(o => o.id));
        const incoming = data.filter(o => !prevIds.has(o.id));
        if (incoming.length > 0) {
          setNewIds(ids => {
            const next = new Set([...ids, ...incoming.map(o => o.id)]);
            setTimeout(() => setNewIds(cur => {
              const s = new Set(cur);
              incoming.forEach(o => s.delete(o.id));
              return s;
            }), 4000);
            return next;
          });
        }
        return data;
      });
      setLastFetch(new Date());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchOrders();
    const t = setInterval(fetchOrders, 3000);
    return () => clearInterval(t);
  }, [fetchOrders]);

  const serveItem = async (itemId) => {
    setServing(prev => new Set([...prev, itemId]));
    try {
      await authFetch(`${BASE}/api/serving/items/${itemId}/serve`, { method: 'PATCH' });
      setOrders(prev => prev.map(o => ({
        ...o,
        items: o.items.map(it =>
          it.order_item_id === itemId ? { ...it, item_status: 'done' } : it
        ),
      })));
    } catch (err) {
      console.error('Failed to serve item:', err);
    } finally {
      setServing(prev => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  };

  const payOrder = async (orderId) => {
    setPaying(prev => new Set([...prev, orderId]));
    try {
      await authFetch(`${BASE}/api/serving/orders/${orderId}/pay`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) {
      console.error('Failed to pay order:', err);
    } finally {
      setPaying(prev => { const s = new Set(prev); s.delete(orderId); return s; });
    }
  };

  const loadOrderDetails = async (orderId) => {
    const res = await authFetch(`${BASE}/api/orders/${orderId}`);
    if (!res.ok) {
      throw new Error('載入訂單失敗');
    }
    const data = await res.json();
    setEditingOrder(data);
    setEditingItems(data.items.map(item => ({
      order_item_id: item.id,
      menu_item_id: item.menu_item_id,
      name: item.name,
      size: item.size,
      unit_price: item.unit_price,
      quantity: item.quantity,
    })));
  };

  const updateQuantity = (index, delta) => {
    setEditingItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], quantity: Math.max(0, next[index].quantity + delta) };
      return next.filter(item => item.quantity > 0);
    });
  };

  const removeItem = (index) => {
    setEditingItems(prev => prev.filter((_, i) => i !== index));
  };

  const submitEdit = async () => {
    if (!editingOrder) return;
    if (editingItems.length === 0) {
      setEditingError('訂單不能沒有餐點');
      return;
    }

    setSavingEdit(true);
    try {
      const res = await authFetch(`${BASE}/api/serving/orders/${editingOrder.id}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: editingItems.map(item => ({
            menu_item_id: item.menu_item_id,
            quantity: item.quantity,
            size: item.size,
          })),
          note: editingOrder.note,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || '更新訂單失敗');
      }
      setEditingOrder(null);
      setEditingItems([]);
      setEditingError('');
      fetchOrders();
    } catch (err) {
      console.error(err);
      setEditingError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchDailyRevenue = async () => {
    try {
      const res = await authFetch(`${BASE}/api/orders/daily-revenue`);
      const data = await res.json();
      setDailyRevenue(data.daily_revenue);
      setShowRevenue(true);
    } catch (err) {
      console.error('Failed to fetch daily revenue:', err);
    }
  };

  return (
    <div className="serving-page">
      <header className="serving-header">
        <div className="serving-header-inner">
          <h1 className="serving-title">🛎 櫃檯端</h1>
          <button className="revenue-btn" onClick={fetchDailyRevenue}>
            💰 今日收入
          </button>
          <button className="logout-btn" onClick={() => { clearToken(); window.location.replace('/'); }}>登出</button>
        </div>
      </header>

      <main className="serving-main">
        {orders.length === 0 ? (
          <div className="serving-empty">
            <div className="empty-icon">✓</div>
            <p>所有餐點皆已送達</p>
          </div>
        ) : (
          <div className="serving-grid">
            {orders.map(order => (
              <div
                key={order.id}
                className={`serving-card ${newIds.has(order.id) ? 'serving-card-new' : ''}`}
              >
                <div className="serving-card-top">
                  <div className="serving-table">
                    <span className="table-icon">{order.table_number === 'out' ? '' : '🪑'}</span>
                    <span className={`table-name${order.table_number === 'out' ? ' table-name-out' : ''}`}>{order.table_number === 'out' ? `外帶 #${order.takeout_seq ?? '?'}` : order.table_number}</span>
                  </div>
                  <span className={`status-badge ${order.status === 'paid' ? 'paid' : allServed(order.items) ? 'ready-all' : 'pending-all'}`}>
                    {order.status === 'paid'
                      ? '已付款'
                      : allServed(order.items)
                        ? '全部完成'
                        : '製作中'
                    }
                  </span>
                </div>

                <ul className="serving-items">
                  {order.items.map((item, i) => (
                    <li key={i} className="serving-item">
                      <span className="serving-item-qty">×{item.quantity}</span>
                      <span className="serving-item-name">
                        {item.name}
                        {sizeLabel(item) && <span className="serving-item-size">{sizeLabel(item)}</span>}
                      </span>
                      {item.item_status === 'done' && (
                        <span className="serving-item-status">完成</span>
                      )}
                      {item.item_status === 'ready' && (
                        <button
                          className="serve-btn"
                          disabled={serving.has(item.order_item_id)}
                          onClick={() => serveItem(item.order_item_id)}
                        >
                          上菜
                        </button>
                      )}
                      {item.item_status === 'cooking' && (
                        <span className="serving-item-status cooking">製作中</span>
                      )}
                      {item.item_status === 'pending' && (
                        <span className="serving-item-status pending">等待中</span>
                      )}
                    </li>
                  ))}
                </ul>

                <div className="serving-total">
                  合計 <span className="serving-total-amount">${Number(order.total).toLocaleString()}</span>
                </div>

                {order.note && (
                  <div className="serving-note">📝 {order.note}</div>
                )}

                <div className="serving-actions">
                  {order.status !== 'paid' && (
                    <button
                      className="edit-btn"
                      onClick={async () => {
                        try {
                          await loadOrderDetails(order.id);
                          setEditingError('');
                        } catch (err) {
                          console.error(err);
                          setEditingError('載入訂單失敗');
                        }
                      }}
                    >
                      修改訂單
                    </button>
                  )}
                  {order.status !== 'paid' && (
                    <button
                      className="checkout-btn"
                      onClick={() => payOrder(order.id)}
                      disabled={paying.has(order.id) || !allServed(order.items)}
                    >
                      {paying.has(order.id) ? '付款中...' : '結單'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editingOrder && (
        <div className="edit-modal-overlay" onClick={() => setEditingOrder(null)}>
          <div className="edit-modal" onClick={e => e.stopPropagation()}>
            <h2>修改訂單</h2>
            <div className="edit-items">
              {editingItems.map((item, index) => (
                <div key={`${item.menu_item_id}-${item.size || ''}`} className="edit-item-row">
                  <span className="edit-item-name">
                    {item.name}
                    {item.size && ` (${item.size === 'S' ? '小' : '中'})`}
                  </span>
                  <div className="edit-item-controls">
                    <button onClick={() => updateQuantity(index, -1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(index, 1)}>+</button>
                    <button className="remove-item-btn" onClick={() => removeItem(index)}>刪除</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="edit-total">
              合計 <span>${editingItems.reduce((sum, it) => sum + it.unit_price * it.quantity, 0).toLocaleString()}</span>
            </div>
            {editingError && <div className="edit-error">{editingError}</div>}
            <div className="edit-actions">
              <button className="save-btn" onClick={submitEdit} disabled={savingEdit}>
                {savingEdit ? '儲存中...' : '儲存'}
              </button>
              <button className="cancel-btn" onClick={() => setEditingOrder(null)}>取消</button>
            </div>
          </div>
        </div>
      )}

      {showRevenue && (
        <div className="revenue-modal-overlay" onClick={() => setShowRevenue(false)}>
          <div className="revenue-modal" onClick={e => e.stopPropagation()}>
            <h3>今日收入</h3>
            <div className="revenue-amount">${dailyRevenue?.toLocaleString()}</div>
            <button className="revenue-close-btn" onClick={() => setShowRevenue(false)}>關閉</button>
          </div>
        </div>
      )}


    </div>
  );
}
